/**
 * OutputStatusList — shared component for the per-output submission status list (Packet E5).
 * Renders one row per pipeline output (id + destination + mode + StatusBadge + timestamp).
 * Falls back to a single legacy status row for documents submitted before per-output
 * tracking existed (Packet E2/E3) — never crashes on a missing `outputs` array.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import StatusBadge, { DocumentStatus } from './StatusBadge';
import { OutputSubmissionStatus } from '../../types';

function outputBadgeStatus(status: OutputSubmissionStatus['status']): DocumentStatus {
  switch (status) {
    case 'SUBMITTED':
      return 'completed';
    case 'FAILED':
      return 'error';
    case 'PENDING':
    default:
      return 'pending';
  }
}

interface LegacySubmissionStatus {
  status?: 'SUCCESS' | 'FAILURE' | 'PARTIAL';
  transactionId?: string;
  submittedAt?: string;
}

function legacyBadgeStatus(status?: LegacySubmissionStatus['status']): DocumentStatus {
  switch (status) {
    case 'SUCCESS':
      return 'completed';
    case 'FAILURE':
      return 'error';
    case 'PARTIAL':
    default:
      return 'pending';
  }
}

interface OutputStatusListProps {
  /** Per-output results — undefined/empty on documents submitted before Packet E2. */
  outputs?: OutputSubmissionStatus[] | null;
  /** Fallback single-status submission record for legacy documents with no `outputs`. */
  legacy?: LegacySubmissionStatus | null;
  /** Timestamp shown when an output row has no per-output timestamp of its own. */
  submittedAt?: string;
}

function formatTimestamp(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

export const OutputStatusList: React.FC<OutputStatusListProps> = ({ outputs, legacy, submittedAt }) => {
  if (outputs && outputs.length > 0) {
    return (
      <Stack spacing={1} role="list" aria-label="Output submission statuses">
        {outputs.map((output) => {
          const timestamp = formatTimestamp(output.batchExportedAt ?? submittedAt);
          return (
            <Paper key={output.outputId} variant="outlined" sx={{ p: 1.5 }} role="listitem">
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {output.outputId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {output.mode === 'api' ? 'Real-time' : 'Batch'} to {output.destinationConnectorId}
                  </Typography>
                </Box>
                <StatusBadge type="document-status" status={outputBadgeStatus(output.status)} />
              </Stack>
              {output.message && output.status === 'FAILED' && (
                <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                  {output.message}
                </Typography>
              )}
              {timestamp && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {timestamp}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Stack>
    );
  }

  // Legacy fallback — single submission status, no per-output breakdown recorded.
  if (legacy && (legacy.status || legacy.submittedAt)) {
    const timestamp = formatTimestamp(legacy.submittedAt ?? submittedAt);
    return (
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" fontWeight={600}>
            Submission
          </Typography>
          <StatusBadge type="document-status" status={legacyBadgeStatus(legacy.status)} />
        </Stack>
        {timestamp && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {timestamp}
          </Typography>
        )}
      </Paper>
    );
  }

  return (
    <Typography variant="caption" color="text.secondary">
      No submission recorded yet.
    </Typography>
  );
};

export default OutputStatusList;
